<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
  // Collapsed composer only has room for the mode icon.
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selectMode']);

const { t } = useI18n();

const isOpen = ref(false);

/**
 * When replies are restricted the composer can only produce private notes,
 * so the trigger reflects that regardless of the stored mode.
 */
const isPrivate = computed(
  () => props.isReplyRestricted || props.mode === REPLY_EDITOR_MODES.NOTE
);

const options = computed(() => [
  {
    label: t('CONVERSATION.REPLYBOX.COMPOSER_MODE.MESSAGE'),
    value: REPLY_EDITOR_MODES.REPLY,
    icon: 'i-ph-chat-circle-dots',
    action: 'select',
    disabled: props.isReplyRestricted,
    isSelected: !isPrivate.value,
  },
  {
    label: t('CONVERSATION.REPLYBOX.COMPOSER_MODE.PRIVATE_NOTE'),
    value: REPLY_EDITOR_MODES.NOTE,
    icon: 'i-ph-note',
    action: 'select',
    isSelected: isPrivate.value,
  },
]);

const activeOption = computed(() =>
  options.value.find(option => option.isSelected)
);

const triggerClass = computed(() =>
  isPrivate.value
    ? 'bg-n-amber-4 text-n-amber-11 font-medium'
    : 'text-n-slate-12 hover:bg-n-alpha-1'
);

const selectMode = value => {
  if (value === REPLY_EDITOR_MODES.REPLY && props.isReplyRestricted) return;
  emit('selectMode', value);
};

const onSelect = ({ value }) => {
  isOpen.value = false;
  if (value === props.mode) return;
  selectMode(value);
};

const toggle = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
};

useKeyboardEvents({
  'Alt+KeyP': {
    action: () => selectMode(REPLY_EDITOR_MODES.NOTE),
    allowOnFocusedInput: false,
  },
  'Alt+KeyL': {
    action: () => selectMode(REPLY_EDITOR_MODES.REPLY),
    allowOnFocusedInput: false,
  },
});
</script>

<template>
  <OnClickOutside class="relative flex-none" @trigger="isOpen = false">
    <button
      type="button"
      data-testid="composer-mode-select"
      aria-haspopup="menu"
      :aria-expanded="isOpen"
      :aria-label="$t('CONVERSATION.REPLYBOX.COMPOSER_MODE.TRIGGER_TOOLTIP')"
      :disabled="disabled"
      class="flex items-center h-8 gap-1.5 text-sm whitespace-nowrap transition-colors rounded-lg disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98] active:duration-75"
      :class="[triggerClass, compact ? 'justify-center size-8' : 'px-2.5']"
      @click="toggle"
      @keydown.esc="isOpen = false"
    >
      <span class="flex-shrink-0 size-4" :class="activeOption.icon" />
      <template v-if="!compact">
        {{ activeOption.label }}
        <span class="flex-shrink-0 i-lucide-chevron-down size-3.5" />
      </template>
    </button>
    <DropdownMenu
      v-if="isOpen"
      :menu-items="options"
      class="w-48 mb-1 bottom-full ltr:left-0 rtl:right-0"
      @action="onSelect"
      @keydown.esc="isOpen = false"
    >
      <template #trailing-icon="{ item }">
        <span
          v-if="item.isSelected"
          class="ltr:ml-auto rtl:mr-auto i-lucide-check size-3.5 text-n-slate-11"
        />
      </template>
    </DropdownMenu>
  </OnClickOutside>
</template>
